import LazyLoader from "./services/LazyLoader";
import { collect } from "collect.js";
import { AxiosResponse } from "axios";
import { useCustomerStore } from "./stores/CustomerStore";
import Axios from "./composables/Axios";
import { defineAsyncComponent, App, createApp } from "vue";
import { useTranslation } from "./composables/Translation";
import { createPinia, mapStores } from "pinia";
import setupI18N from "./i18n";
import { useFormatsMoney } from "./composables/FormatsMoney";
import { useFormatsNumbers } from "./composables/FormatsNumbers";
import ProductEventBus from "./EventBus/ProductEventBus";
import { CheckoutSteps, NextStepStates, useCheckoutStore } from "./stores/CheckoutStore";
import { useProductFilterWidgetHelper } from "./ProductFilterWidgetHelper";
import { useBasketStore } from "./stores/BasketStore";
import { useInterfaceStore } from "./stores/InterfaceStore";
import { useCustomizationCheckoutStore } from "../../nubuiten/ts/stores/CustomizationCheckoutStore";
import { useProductDetailStore } from "./stores/ProductDetailStore";
import GlobalEventBus from "./EventBus/GlobalEventBus";
import { usePriceHelper } from "./composables/PriceHelper";
import { useGA4 } from "./composables/GA4";
import ScrollBooster from "scrollbooster";
import ApiService from "./composables/ApiService";
import { usePropertyHelper } from "./composables/PropertyHelper";

function setupVueApp(): App<Element> {
    const { exclVat } = usePriceHelper();
    const { trackGA404, trackGAPageView } = useGA4();

    return createApp({
        data() {
            return {
                // TODO: Can the line below be removed?
                menuClass: false,
                // TODO: Can the line below be removed?
                mainMenuClass: false,
                wordpressUsername: null,
                ...useFormatsMoney(),
                ...useFormatsNumbers(),
                ProductEventBus: ProductEventBus,
                NextStepStates: NextStepStates,
                CheckoutSteps: CheckoutSteps,
                productFilterWidgetHelper: useProductFilterWidgetHelper(),
                exclVat: exclVat,
                ga4PurchaseEventWasLogged: false,
                ga4PageViewEventWasLogged: false,
            };
        },
        computed: {
            ...mapStores(
                useCustomerStore,
                useBasketStore,
                useInterfaceStore,
                useCheckoutStore,
                useCustomizationCheckoutStore,
                useProductDetailStore,
            ),
        },
        created() {
            // @ts-ignore TODO: FIX THIS UP PROPER
            window.$t = this.$t;

            // Fetch the Customer
            this.customerStore.init().then(() => {
                // Only once the customer is fetched, fetch the username of the currently logged in WordPress user.
                // This is done so that the request below doesn't trigger fetching a customer token (because that fails).
                Axios.getInstance()
                    .get("/wp-username")
                    .then((response) => {
                        this.wordpressUsername = response.data;
                    });
            });

            // Fetch the Basket
            this.basketStore.init().then(() => {
                this.ga4HandleSuccessPage();
            });

            // Fetch the product detail Product
            this.productDetailStore.init();

            document.addEventListener("DOMContentLoaded", () => {
                if (this.basketStore.basket) {
                    this.ga4HandleSuccessPage();
                }

                if (this.customerStore.isInitialized) {
                    trackGAPageView();
                    this.ga4PageViewEventWasLogged = true;
                } else {
                    this.customerStore.$subscribe(() => {
                        if (!this.ga4PageViewEventWasLogged && this.customerStore.isInitialized) {
                            trackGAPageView();
                            this.ga4PageViewEventWasLogged = true;
                        }
                    });
                }
            });

            window.onYouTubeIframeAPIReady = function () {
                this.interfaceStore.isYouTubeIframeAPIReady = true;
            }.bind(this);
        },
        mounted() {
            GlobalEventBus.on("toggleMobileMenu", this.toggleMenuOpenClass);
        },
        methods: {
            ga4HandleSuccessPage() {
                if (this.ga4PurchaseEventWasLogged) {
                    return;
                }

                // Handle GA4 on checkout success page
                // Detect checkout success page
                let checkoutSuccessData = document.querySelector("script[id=checkout-success-data]")?.innerHTML;
                if (checkoutSuccessData) {
                    let parsedCheckoutSuccessData: { transactionId: string | null } = JSON.parse(
                        checkoutSuccessData.replaceAll(" ", "").replaceAll("\n", ""),
                    );

                    this.ga4PurchaseEventWasLogged = true;
                    this.checkoutStore.onSuccessPageLoaded(parsedCheckoutSuccessData?.transactionId);
                }
            },
            toggleMenuOpenClass() {
                const el = document.body;
                if (this.interfaceStore.menuClass === "mobile-menu-open") {
                    this.interfaceStore.changeMenuClass("");
                    el.classList.remove("mobile-menu-open");
                } else {
                    this.interfaceStore.changeMenuClass("mobile-menu-open");
                    el.classList.add("mobile-menu-open");
                }
            },
        },
    });
}

function setupEventListeners() {
    if (window.klevuActive) {
        window.addEventListener(
            "klevu_addtocart_trigger",
            (event: CustomEvent<{ id: string; url: string; qty: number }>) => {
                const { id, url, qty } = event.detail;
                fetchAndAddToCart(id);
            },
        );

        async function fetchAndAddToCart(productId: string) {
            try {
                const apiService: ApiService = new ApiService();
                const basketStore = useBasketStore();
                const propertyHelper = usePropertyHelper();

                const params = {
                    "filter[store_ids]": +window.store_id,
                    "filter[id]": productId,
                };

                const response = await apiService.get(`/v2/products?includes=media.versions,properties`, { params });

                const product = response?.data?.data?.products?.[0];

                if (!product) {
                    console.warn(`Product with ID ${productId} not found.`);
                    return;
                }

                const minimalQuantity =
                    parseFloat(propertyHelper.getProperty(product.properties ?? [], "minimaleafname")?.value) || 1;

                basketStore.addEntry(
                    {
                        product,
                        amount: minimalQuantity,
                        chosen_options: {},
                    },
                    true,
                    true,
                );
            } catch (error) {
                console.error("Error adding product to cart:", error);
            }
        }
    }

    document.addEventListener("DOMContentLoaded", function () {
        const { trackGA404 } = useGA4();

        // Detect 404 page
        if (document.querySelector("body").classList.contains("404")) {
            // Google Analytics 404 event
            trackGA404(window.location.pathname);
        }

        // TODO: Is this still in use or can it be removed?
        let scrollBoostersX = document.getElementsByClassName("scroll-booster-x");
        for (let i = 0; i < scrollBoostersX.length; i++) {
            let isRunning = false;
            let scrollBooster = scrollBoostersX.item(i);
            let viewport = scrollBooster.querySelector(".scroll-booster-x-viewport");
            if (viewport) {
                let content = viewport.querySelector(".scroll-booster-x-content");
                let scrollbar = scrollBooster.querySelector(".scroll-booster-x-bar");
                if (content) {
                    let sb = new ScrollBooster({
                        viewport,
                        content,
                        mode: "x",
                        onUpdate: (data) => {
                            isRunning = data.isRunning;
                            // @ts-ignore // TODO: remove this @ts-ignore
                            content.style.transform = `translateX(${-data.position.x}px)`;
                            if (scrollbar) {
                                scrollbar.scrollLeft = data.position.x;
                            }
                        },
                        onClick: (data, event) => {
                            if (data.dragOffsetPosition.x) {
                                event.preventDefault();
                            }
                        },
                    });

                    if (scrollbar) {
                        let scrollbarContent = scrollbar.querySelector("div");
                        scrollbarContent.style.width = content.getBoundingClientRect().width + "px";
                        scrollbar.addEventListener(
                            "scroll",
                            (event) => {
                                if (!isRunning) {
                                    sb.setPosition({
                                        // @ts-ignore // TODO: remove this @ts-ignore
                                        x: event.target.scrollLeft,
                                        y: 0,
                                    });
                                }
                            },
                            { passive: true },
                        );
                    }
                }
            }
        }
    });
}

function setupGlobalBindings() {
    const { __t } = useTranslation();
    // Local cache persists during current request. TODO: remove this 'cache' object
    window.cache = {};
    // @ts-ignore
    window.__t = __t;
    window.lazyLoader = new LazyLoader();
}

/**
 * @deprecated
 * @todo All of this initialisation should just happen inside the Axios singleton class.
 */
function setupAxios() {
    /**
     * We will register the CSRF Token as a common header with Axios so that
     * all outgoing HTTP requests automatically have it attached. This is just
     * a simple convenience so we don't have to attach every token manually.
     */
    Axios.getInstance().defaults.params = {};
    Axios.getInstance().defaults.responseType = "json";
    (Axios.getInstance().defaults.headers as Record<string, any>).common["X-Requested-With"] = "XMLHttpRequest";
    (Axios.getInstance().defaults.headers as Record<string, any>).common["X-Daalder-Store"] = window.store_id;
    Axios.getInstance().defaults.headers["X-Daalder-Language"] = window.daalder_locale;

    // TODO: All of this initialisation should just happen inside the Axios singleton class.
    // apply interceptor on response
    Axios.getInstance().interceptors.response.use(
        (response: any) => response,
        function (error: any) {
            if (error.config && !error.config?.isRetried && error.response && error.response.status === 403) {
                const customerStore = useCustomerStore();
                return customerStore.logoutAndFetchAnonymousToken().then((data: AxiosResponse) => {
                    // @ts-ignore TODO: remove this ts-ignore
                    error.config.headers["X-Daalder-Customer-Token"] = data.customer_token;
                    error.config.isRetried = true;
                    return Axios.getInstance().request(error.config);
                });
            }
            return Promise.reject(error);
        },
    );

    // TODO: All of this initialisation should just happen inside the Axios singleton class.
    Axios.getInstance().interceptors.response.use(function (response: AxiosResponse) {
        return response;
    });
}

function mountVue(app: App<Element>) {
    setupI18N(window.wordpress_locale).then((i18nPlugin) => {
        app.use(i18nPlugin);
        app.mount("#app");
    });
}

function registerVueExtensions(app: App<Element>) {
    // Register global directives
    const VueScrollTo = require("vue-scrollto");
    const VueNumber = defineAsyncComponent(() => import(/* webpackChunkName: "VueNumber" */ "vue-number-animation"));
    const vClickOutside = defineAsyncComponent(() => import(/* webpackChunkName: "vClickOutside" */ "v-click-outside"));
    app.directive("clickOutside", vClickOutside.directive);
    app.use(VueScrollTo);
    app.use(VueNumber);

    // Register pinia & i18n. This MUST happen just before mounting the app, otherwise the Vue devtools for them don't work.
    // It is unknown why this is the case.
    const pinia = createPinia();
    app.use(pinia);

    // Check the wordpress_locale before mounting the Vue app. It's required for a pre-init step (i18n).
    if (window.wordpress_locale) {
        mountVue(app);
    } else {
        // Wait for the wordpress_locale before mounting the Vue app. The check interval is very low because the Vue
        // app mounting should happen ASAP, because much of the site's content is displayed using Vue.
        const interval = setInterval(() => {
            if (window.wordpress_locale) {
                clearInterval(interval);
                mountVue(app);
            }
        }, 5);
    }
}

export default function baseBootstrap(): App<Element> {
    const app = setupVueApp();

    setupEventListeners();
    setupGlobalBindings();
    setupAxios();
    registerVueExtensions(app);

    return app;
}
